import { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatDateTime } from 'medere-ui-framework';
import { MailingReportMessageStateDTO } from '../../dto/MailingReportDTO';
import { getMailingReportMessageStates } from '../../services/MailingReportController';
import { parseApiError } from '../../services/apiError';

/**
 * La descripción del cambio de estado viene con HTML desde messagesender.
 * Se muestra como texto plano (sin ejecutar ni renderizar marcado), respetando los saltos de línea.
 */
function htmlToText(html: string | null): string {
    if (!html) return '—';
    const withLineBreaks = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n');
    const text = new DOMParser().parseFromString(withLineBreaks, 'text/html').body.textContent ?? '';
    return text.trim() || '—';
}

interface MailingReportMessageStatesProps {
    messageId: number;
}

/** Historial de estados de un mensaje. Se muestra como fila expandida del detalle (nunca un modal sobre otro). */
function MailingReportMessageStates({ messageId }: MailingReportMessageStatesProps) {
    const [states, setStates] = useState<MailingReportMessageStateDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getMailingReportMessageStates(messageId)
            .then(data => { if (!cancelled) { setStates(data); setError(null); } })
            .catch(e => { if (!cancelled) setError(parseApiError(e).message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [messageId]);

    if (error) {
        return <p className="mailing-report__history-error" role="alert">{error}</p>;
    }

    return (
        <div className="mailing-report__history">
            <h3 className="mailing-report__history-title">Historial del mensaje</h3>
            <DataTable
                value={states}
                loading={loading}
                className="mailing-report__table"
                emptyMessage="El mensaje no tiene cambios de estado registrados."
            >
                <Column field="state" header="Estado" body={(row: MailingReportMessageStateDTO) => row.state ?? '—'} />
                <Column field="dateChanged" header="Fecha" className="mailing-report__cell-center" headerClassName="mailing-report__cell-center"
                    body={(row: MailingReportMessageStateDTO) => row.dateChanged ? formatDateTime(row.dateChanged) : '—'} />
                <Column field="description" header="Descripción" className="mailing-report__cell-multiline"
                    body={(row: MailingReportMessageStateDTO) => htmlToText(row.description)} />
            </DataTable>
        </div>
    );
}

export default MailingReportMessageStates;
